/**
 * OverduePaymentRecheck · iter303ak-fix39 — Overdue · T10 · Action 10
 * ────────────────────────────────────────────────────────────────────
 * Combines Westpac quick-launch + per-child payment capture (reuses
 * invoiced_payments data) with two Overdue-specific superpowers:
 *   1. **Move to Paid pill** (Q1a) — bright green when all children
 *      show `status: 'paid'` (or are written-off). One click PATCHes
 *      appraisal_status → paid and calls onNext() to bail out of the
 *      remaining Overdue tasks.
 *   2. **Write Off toggle** per child (Q3a) — marks a child as
 *      `written_off: true` on `invoiced_payments[child_id]`. Written-
 *      off children count as settled for routing but are visually
 *      badged red.
 *
 * All payment CRUD identical to InvoicedPaymentCapture — same data
 * shape, same endpoint, same summary math.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, Plus, Trash2, CheckCircle2, AlertCircle, Building2, Zap, XOctagon } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const uid = () => `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function OverduePaymentRecheck({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;
  const parent = useMemo(() => (capturedData?.appraisal_record || capturedData?.appraisal || {}), [capturedData]);

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const depositPick = useMemo(() => (parent.per_child_deposit_pick || {}), [parent]);
  const extras = useMemo(() => (parent.work_execution?.extras || []), [parent]);
  const extrasTags = useMemo(() => (parent.extras_contractor_tags || {}), [parent]);

  const [state, setState] = useState(value?.state || parent.invoiced_payments || {});
  const [rowsMeta, setRowsMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [movingToPaid, setMovingToPaid] = useState(false);
  const [err, setErr] = useState('');

  const loadDues = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const meta = {};
    for (const c of acceptedChildren) {
      let base = 0;
      try {
        const { data: overview } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
        for (const sec of (overview?.sections || [])) {
          for (const ln of (sec?.lines || [])) {
            if (ln?.excluded_from_report) continue;
            base += Number(ln?.client_line_total || (Number(ln?.materials_total || 0) + Number(ln?.contractor_share || 0)) || 0);
          }
        }
      } catch { /* silent */ }
      let tagged_extras = 0;
      for (const ex of extras) {
        const tag = extrasTags[ex.id] || {};
        if (String(tag.child_id || '') === String(c.child_id)) tagged_extras += Number(ex.cost || 0);
      }
      const full = base + tagged_extras;
      const dep = Number(depositPick[c.child_id]?.amount || 0);
      meta[c.child_id] = { due_amount: Number((full - dep).toFixed(2)) };
    }
    setRowsMeta(meta);
    setLoading(false);
  }, [acceptedChildren, extras, extrasTags, depositPick]);
  useEffect(() => { loadDues(); }, [loadDues]);

  // Compute per-child status accounting for written_off.
  const summary = useMemo(() => {
    const s = {};
    for (const c of acceptedChildren) {
      const bucket = state[c.child_id] || {};
      const payments = bucket.payments || [];
      const paid = payments.reduce((a, p) => a + Number(p.amount || 0), 0);
      const due = rowsMeta[c.child_id]?.due_amount || 0;
      const remaining = Number((due - paid).toFixed(2));
      const written_off = !!bucket.written_off;
      let status = 'unpaid';
      if (written_off) status = 'written_off';
      // iter303ak-fix40 · Child with $0 owing counts as settled — Trevor's
      // Guttering A5212 case where no lines/deposit means nothing to pay.
      else if (due <= 0.01) status = 'paid';
      else if (paid > 0 && remaining <= 0.01) status = 'paid';
      else if (paid > 0) status = 'partial';
      s[c.child_id] = { due_amount: due, paid_total: Number(paid.toFixed(2)), remaining, status, written_off, payments };
    }
    return s;
  }, [acceptedChildren, state, rowsMeta]);

  useEffect(() => { if (onChange) onChange({ state: summary }); }, [summary]);  // eslint-disable-line react-hooks/exhaustive-deps

  const allSettled = acceptedChildren.length > 0 && acceptedChildren.every(c => {
    const s = summary[c.child_id]?.status;
    return s === 'paid' || s === 'written_off';
  });
  const anyPaid = acceptedChildren.some(c => summary[c.child_id]?.status === 'paid');

  const addPayment = (child_id) => setState(prev => {
    const bucket = prev[child_id] || { payments: [] };
    return { ...prev, [child_id]: { ...bucket, payments: [...(bucket.payments || []), {
      id: uid(), amount: '', reference: '', code: '', bank: '',
      date: new Date().toISOString().slice(0, 10), added_at: new Date().toISOString(),
    }] } };
  });
  const patchPayment = (child_id, payment_id, patch) => setState(prev => {
    const bucket = prev[child_id] || { payments: [] };
    return { ...prev, [child_id]: { ...bucket, payments: (bucket.payments || []).map(p => p.id === payment_id ? { ...p, ...patch } : p) } };
  });
  const deletePayment = (child_id, payment_id) => setState(prev => {
    const bucket = prev[child_id] || { payments: [] };
    return { ...prev, [child_id]: { ...bucket, payments: (bucket.payments || []).filter(p => p.id !== payment_id) } };
  });
  const toggleWriteOff = (child_id) => setState(prev => {
    const bucket = prev[child_id] || { payments: [] };
    return { ...prev, [child_id]: { ...bucket, written_off: !bucket.written_off,
      written_off_at: !bucket.written_off ? new Date().toISOString() : null } };
  });

  const persist = useCallback(async () => {
    if (!parentId) return;
    setSaving(true); setErr('');
    try {
      await api.patch(`/appraisals/${parentId}`, { invoiced_payments: summary });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  }, [parentId, summary]);

  const moveToPaid = async () => {
    setMovingToPaid(true); setErr('');
    try {
      await persist();
      // W-B4 fix (2026-02): route status transition through the /status
      // endpoint so log_audit fires + all Paid-status side effects run.
      // Direct inline-field PATCH bypassed the audit trail — the most
      // important transition (Paid) was silently unlogged.
      await api.patch(`/appraisals/${parentId}/status`, {
        appraisal_status: 'Paid',
        via: 'workflow_runner',
      });
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Move to Paid failed');
    } finally { setMovingToPaid(false); }
  };

  const persistAndContinue = async () => {
    // iter303ak-fix43 · If everything settled, auto-route to Paid instead
    // of proceeding into T20 Contact Client. Mirrors the "Move to Paid" pill.
    if (allSettled) return moveToPaid();
    await persist();
    onNext();
  };

  return (
    <div className="space-y-4" data-testid="wf-overdue-recheck">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Re-check Payments</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Log any new payments received since Invoiced T30. If all children are settled, the green Move to Paid pill unlocks.'}
            </div>
          </div>
        </div>
      </div>

      <a href="https://www.westpac.co.nz/olb/" target="_blank" rel="noopener noreferrer"
        data-testid="wf-overdue-westpac-link"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm">
        <Building2 size={14} /> Open Westpac Online Banking
        <span className="text-xs opacity-80">↗ new tab</span>
      </a>

      {allSettled && anyPaid && (
        <button onClick={moveToPaid} disabled={movingToPaid}
          data-testid="wf-overdue-move-to-paid"
          className="w-full p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold shadow-lg text-left flex items-center gap-3">
          <Zap className="w-6 h-6" />
          <div className="flex-1">
            <div className="text-lg">Move to Paid</div>
            <div className="text-xs font-normal opacity-90">
              All children fully paid or written off — click to skip remaining Overdue tasks and fire Certificate of Completion.
            </div>
          </div>
          {movingToPaid ? <span>Moving…</span> : <CheckCircle2 className="w-6 h-6" />}
        </button>
      )}

      {loading && <div className="p-4 text-sm text-slate-500 italic">Loading due amounts…</div>}

      {!loading && acceptedChildren.map(c => {
        const s = summary[c.child_id] || { due_amount: 0, paid_total: 0, remaining: 0, status: 'unpaid' };
        const payments = state[c.child_id]?.payments || [];
        const isWrittenOff = s.status === 'written_off';
        const isPaid = s.status === 'paid';
        return (
          <div key={c.child_id} className={`border rounded-lg overflow-hidden
            ${isWrittenOff ? 'border-rose-300 bg-rose-50/30' :
              isPaid ? 'border-emerald-300 bg-emerald-50/30' :
              s.status === 'partial' ? 'border-amber-300 bg-amber-50/30' :
              'border-slate-300 bg-white'}`}
            data-testid={`wf-overdue-child-${c.child_id}`}>
            <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-800">
                {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
              </span>
              {c.appraisal_no && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                  A{c.appraisal_no}
                </span>
              )}
              <span className={`text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 border
                ${isWrittenOff ? 'bg-rose-100 border-rose-300 text-rose-800' :
                  isPaid ? 'bg-emerald-100 border-emerald-300 text-emerald-800' :
                  s.status === 'partial' ? 'bg-amber-100 border-amber-300 text-amber-800' :
                  'bg-slate-100 border-slate-300 text-slate-700'}`}>
                {isWrittenOff ? 'Written off' : isPaid ? '✓ Fully paid' : s.status === 'partial' ? 'Partial' : 'Unpaid'}
              </span>
              <div className="ml-auto flex items-center gap-3 text-xs">
                <span className="text-slate-500">Due <b className="font-mono text-slate-800">${fmt(s.due_amount)}</b></span>
                <span className="text-slate-500">Paid <b className="font-mono text-emerald-700">${fmt(s.paid_total)}</b></span>
                <span className={s.remaining > 0.01 && !isWrittenOff ? 'text-rose-700' : 'text-slate-500'}>
                  Remaining <b className="font-mono">${fmt(s.remaining)}</b>
                </span>
                <button onClick={() => toggleWriteOff(c.child_id)}
                  data-testid={`wf-overdue-writeoff-${c.child_id}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-1 border
                    ${isWrittenOff
                      ? 'text-white bg-rose-600 border-rose-700 hover:bg-rose-700'
                      : 'text-rose-700 bg-white border-rose-300 hover:bg-rose-50'}`}>
                  <XOctagon size={10} /> {isWrittenOff ? 'Un-write off' : 'Write off'}
                </button>
              </div>
            </div>
            {!isWrittenOff && (
              <div className="p-3 space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-2 bg-white border border-slate-200 rounded"
                    data-testid={`wf-overdue-payment-row-${c.child_id}-${p.id}`}>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Amount</label>
                      <input type="number" step="0.01" value={p.amount}
                        onChange={(e) => patchPayment(c.child_id, p.id, { amount: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Reference</label>
                      <input type="text" value={p.reference}
                        onChange={(e) => patchPayment(c.child_id, p.id, { reference: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Code</label>
                      <input type="text" value={p.code}
                        onChange={(e) => patchPayment(c.child_id, p.id, { code: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Bank</label>
                      <input type="text" value={p.bank}
                        onChange={(e) => patchPayment(c.child_id, p.id, { bank: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Date</label>
                      <input type="date" value={p.date}
                        onChange={(e) => patchPayment(c.child_id, p.id, { date: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      <button onClick={() => deletePayment(c.child_id, p.id)}
                        className="p-1.5 rounded text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => addPayment(c.child_id)}
                  data-testid={`wf-overdue-payment-add-${c.child_id}`}
                  className="px-3 py-1 rounded text-xs font-semibold text-sky-700 bg-white border border-sky-300 hover:bg-sky-50 inline-flex items-center gap-1">
                  <Plus size={12} /> Add payment
                </button>
              </div>
            )}
            {isWrittenOff && (
              <div className="p-3 bg-rose-50/50 text-xs text-rose-800 italic border-t border-rose-200">
                This child is marked as bad debt / written off. Excluded from further chasing and routing math.
              </div>
            )}
          </div>
        );
      })}

      {err && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
        <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
      </div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2 items-center">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={saving || movingToPaid}
            data-testid="wf-overdue-recheck-continue"
            className={`px-4 py-1.5 rounded-md text-sm text-white disabled:opacity-40 inline-flex items-center gap-2 ${allSettled ? 'bg-emerald-700 hover:bg-emerald-800 ring-2 ring-emerald-300' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
            <CheckCircle2 className="w-4 h-4" />
            {saving || movingToPaid ? 'Saving…' : (allSettled ? 'Save & Move to Paid' : 'Save & Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
